import { useMemo, useState } from 'react';
import { CollapsibleConfigSection } from '@/components/shared/CollapsibleConfigSection';
import { AgentSettingsChipContainer } from '@/components/agent/AgentSettingsChipContainer';
import { AgentAttachmentsEditor } from '@/components/agent/AgentAttachmentsEditor';
import { AdvancedConfigChips } from '@/components/agent/AdvancedConfigChips';
import { useBlueprints } from '@/hooks/blueprints/useBlueprints';
import { useAvailableBlueprints } from '@/hooks/blueprints/useAvailableBlueprints';
import {
  GIT_REPO_ATTACHMENT_ID,
  type GitRepoAttachmentConfig,
  type CustomSandboxSize,
  type ResourceSize,
} from '@runloop/reflex-contract';
import { type AgentModelEndpoint, type Snapshot } from '@reflex/shared';
import { snapshotIdFromPickerValue, snapshotPickerValue } from './snapshot-picker-value';
import type { AgentEnvVar, AgentType, PluginAttachmentValue } from '@/lib/types';
import type { AvailableSecretsOwner } from '@/components/agent/env-vars/SecretRefPicker';
import { resolveBlueprintName } from './resolve-blueprint-name';

type BlueprintSelection = { mode: 'id'; value: string | null } | { mode: 'name'; value: string };

interface AgentConfigFormSectionBaseProps {
  agentType: AgentType;
  /**
   * `null` is the same as "no override" — the underlying matrix may
   * resolve a different effective provider based on the model. Pass the
   * user's deliberate selection here.
   */
  selectedProviderId: string | null;
  model: string;
  modelEndpointId: string | null;
  reasoningEffort: string | null;
  attachmentValues: Record<string, PluginAttachmentValue>;
  envVars: AgentEnvVar[];
  /**
   * Devbox resource size, picked from {@link SANDBOX_RESOURCE_SIZE_OPTIONS}.
   * `null` means "use the blueprint's default size" — the agent runner
   * falls back to the sandbox-provider's defaults.
   */
  resourceSize?: ResourceSize | null;
  /** Selected org-defined custom size, mutually exclusive with `resourceSize`. */
  customSize?: CustomSandboxSize | null;
  /** The org's custom sizes offered in the size picker. */
  customSizes?: CustomSandboxSize[];
  /** The org's default-size setting; names the size chip's "(Default)" option. */
  defaultSandboxSize?: string | null;
  /** Whether the active Runloop account is subject to the trial size cap. */
  isTrial?: boolean;
  /**
   * Whether to start a Docker daemon (Docker-in-Docker) inside the devbox at
   * launch, surfaced as the "dockerd" capability chip. `false`/omitted leaves
   * docker unavailable. Only rendered when `onDockerdChange` is supplied.
   */
  dockerd?: boolean;
  /**
   * Whether to enable computer use / browser use (a virtual desktop) at
   * launch, surfaced in the "Capabilities" chip. Only rendered when
   * `onComputerUseChange` is supplied — callers pass it only when the org
   * has the computer-use plugin installed.
   */
  computerUse?: boolean;
  /**
   * Minutes the devbox may sit idle before auto-suspending. `null`/omitted
   * defers to the provider default. Only rendered when
   * `onIdleTimeMinutesChange` is supplied.
   */
  idleTimeMinutes?: number | null;
  /**
   * Whether the form is editing an entity that already has secret env-vars
   * stored (so the editor can show a "stored secret" badge instead of the
   * "enter value" form). Defaults to `false`.
   */
  hasStoredSecrets?: boolean;
  onAgentTypeChange: (next: AgentType) => void;
  onModelChange: (modelId: string) => void;
  onReasoningEffortChange: (effort: string) => void;
  onEndpointChange: (endpointId: string) => void;
  onBlueprintChange: (next: string | null) => void;
  /**
   * Org devbox snapshots offered in the blueprint picker. Callers fetch and
   * capability-gate these (`devbox-snapshots`); omit/empty hides the group.
   * Requires `onSnapshotChange` to take effect.
   */
  snapshots?: Snapshot[];
  /** Currently-selected snapshot id (`snp_*`), or `null`. */
  snapshotId?: string | null;
  /**
   * Snapshot selection callback. Picking a snapshot clears the blueprint
   * (and vice versa) — the server rejects launches that set both.
   */
  onSnapshotChange?: (next: string | null) => void;
  onResourceSizeChange?: (next: ResourceSize | null) => void;
  onCustomSizeChange?: (next: CustomSandboxSize | null) => void;
  /**
   * Toggle the "dockerd" capability. When omitted, the chip is hidden (e.g.
   * surfaces that don't support Docker-in-Docker).
   */
  onDockerdChange?: (next: boolean) => void;
  /**
   * Toggle the "computer use" capability. When omitted, the toggle is hidden
   * (e.g. orgs without the computer-use plugin installed).
   */
  onComputerUseChange?: (next: boolean) => void;
  /**
   * Set the idle-time minutes before auto-suspend. When omitted, the
   * Auto-suspend chip is hidden.
   */
  onIdleTimeMinutesChange?: (next: number | null) => void;
  onAttachmentChange: (id: string, config: unknown, pluginName: string) => void;
  onEnvVarsChange: (next: AgentEnvVar[]) => void;
  /**
   * Labels (`alb_*`) and group (`agp_*`) the configuration files its runs
   * under. Each chip renders only when its handler is supplied, so a surface
   * that has no organization catalog to read (Storybook fixtures) omits them.
   */
  labelIds?: string[];
  onLabelIdsChange?: (next: string[]) => void;
  groupId?: string | null;
  onGroupIdChange?: (next: string | null) => void;
  /**
   * Owner context for the secret picker inside the embedded
   * `AgentAttachmentsEditor`. Passed verbatim to the picker so
   * user/team-tier rows scope to the right identity:
   * - Launch dialog leaves it undefined (picker falls back to launching user).
   * - User-owned persona editor: `{ ownerType: 'user', ownerUserId }`.
   * - Team-owned persona editor: `{ ownerType: 'team', ownerTeamId }`.
   * - Org-owned persona editor: `{ ownerType: 'organization' }`.
   */
  secretsOwner?: AvailableSecretsOwner;
  /**
   * Endpoints the caller considers launchable, overriding the server's
   * host-credential availability view. The Launch dialog passes a list that
   * accounts for a user-supplied provider key (which injects the endpoint's
   * secret-backed credential at launch), so the "no compatible endpoint"
   * notice clears once a satisfying key is picked. When omitted (e.g. the
   * persona editor), falls back to server-reported availability.
   */
  launchableEndpointsOverride?: AgentModelEndpoint[];
  /**
   * Currently-selected model-provider secret (`mps_*`) pinned to the
   * config, or `null`. When `onProviderSecretChange` is supplied the
   * section renders a `ModelProviderSecretPicker` scoped to the resolved
   * provider; omit both to hide the picker (e.g. the launch dialog renders
   * its own and reflects key state in the endpoint list).
   */
  providerSecretId?: string | null;
  onProviderSecretChange?: (id: string | null) => void;
  /**
   * Owner of the resource the key is bound to. Drives owner-scoped key
   * filtering in the picker: a `user`-owned entity may use user+team+org
   * keys, `team` -> team+org, `organization` -> org-only.
   */
  providerKeyOwner?: {
    ownerId: string;
  };
  /**
   * When true, the section does not render its own `AgentSettingsChip`. Launch
   * composers set this and render the chip in the `ChatInput` toolbar instead;
   * the Persona editor leaves it false so the chip stays inline.
   */
  hideSettingsChip?: boolean;
  /**
   * When true, the "Attachments" sub-section (the per-kind `AgentAttachmentsEditor`
   * grid) is not rendered. Launch composers set this because attachments are
   * added through the composer's "+"/`@` menu and managed from the chips in the
   * applied-context row, whose disclosure hosts the same plugin picker this
   * grid would. The Persona editor and Flow panel leave it false (they have no
   * composer, so the grid is their only way to attach).
   *
   * Those chips have to stay a full management surface while this is set: when
   * they were display-only, a multi-value attachment could only be cleared
   * wholesale, so removing one of six MCP servers meant re-adding the other
   * five one mention at a time.
   */
  hideAttachments?: boolean;
  /**
   * Skip the collapsible Attachments/Advanced headers and render the
   * editors directly (advanced chips, then attachments). The flow inspector
   * supplies its own section chrome around this block.
   */
  bare?: boolean;
  /**
   * When true, the "Advanced" section starts collapsed even when the incoming
   * config already overrides something inside it. Launch composers set this so
   * the launcher opens compact every time: a remembered draft, an applied
   * persona, or "Launch again" carries sandbox settings often enough that the
   * section was expanded more often than not, pushing the prompt up the dialog.
   * The "customized" badge on the collapsed header carries that signal instead.
   * The Persona editor and Flow panel leave it false — there the section holds
   * the config being edited, so a stored override should be on screen as soon as
   * the form opens.
   */
  advancedStartsCollapsed?: boolean;
  /**
   * After a blocked submit (provider needs a key, none selected), flip this on
   * so the settings chip shows an explicit missing-key status. See
   * {@link AgentSettingsChipContainer}'s prop of the same name.
   */
  showMissingKeyError?: boolean;
  /**
   * Status copy when {@link showMissingKeyError} is true. Persona save
   * overrides the launch default.
   */
  missingKeyErrorText?: string;
}

export type AgentConfigFormSectionProps = AgentConfigFormSectionBaseProps &
  (
    | {
        /** The selected blueprint name, or `null` for "no blueprint". */
        blueprintName: string | null;
        blueprintSelection?: never;
      }
    | {
        /**
         * Either a blueprint id or name. The change callback returns the
         * same kind of value selected here.
         */
        blueprintSelection: BlueprintSelection;
        blueprintName?: never;
      }
  );

/**
 * Shared "agent configuration" form section, used by both the Launch
 * dialog and the Persona edit form.
 *
 * Renders three sub-sections inside a single card:
 *   1. Agent type / provider / model / reasoning / endpoint + provider key,
 *      packaged into the shared, popover-driven `AgentSettingsChip`.
 *      Matrix-aware — uses `useAgentModelSupportHelpers` to filter the
 *      available providers and endpoints.
 *   2. Blueprint picker. Shows all blueprints when no repo is selected,
 *      then filters to repo-scoped blueprints once the `git-repo` plugin
 *      attachment has a repo.
 *   3. `AgentAttachmentsEditor` for plugin attachments + env vars.
 *
 * Both forms own their own state (the launch dialog uses a reducer; the
 * persona form uses RHF + local state). This component is a pure renderer
 * that receives the current values and dispatches changes back through
 * the `on*Change` callbacks.
 */
export function AgentConfigFormSection({
  agentType,
  selectedProviderId,
  model,
  modelEndpointId,
  reasoningEffort,
  blueprintName,
  blueprintSelection,
  resourceSize = null,
  customSize = null,
  customSizes,
  defaultSandboxSize = null,
  isTrial = false,
  dockerd = false,
  computerUse = false,
  idleTimeMinutes = null,
  attachmentValues,
  envVars,
  hasStoredSecrets = false,
  onAgentTypeChange,
  onModelChange,
  onReasoningEffortChange,
  onEndpointChange,
  onBlueprintChange,
  snapshots,
  snapshotId = null,
  onSnapshotChange,
  onResourceSizeChange,
  onCustomSizeChange,
  onDockerdChange,
  onComputerUseChange,
  onIdleTimeMinutesChange,
  onAttachmentChange,
  onEnvVarsChange,
  labelIds,
  onLabelIdsChange,
  groupId = null,
  onGroupIdChange,
  secretsOwner,
  launchableEndpointsOverride,
  providerSecretId,
  onProviderSecretChange,
  providerKeyOwner,
  hideSettingsChip = false,
  hideAttachments = false,
  bare = false,
  advancedStartsCollapsed = false,
  showMissingKeyError = false,
  missingKeyErrorText,
}: AgentConfigFormSectionProps) {
  const { data: blueprints = [] } = useBlueprints();

  // The repo carried in the `git-repo` attachment ranks the blueprint list —
  // ones built for it lead, the rest stay selectable below. Both forms store
  // the repo this way (it's the canonical place), so pulling it back out keeps
  // blueprint suggestions in sync with whatever the user picked in the
  // attachments editor.
  const repoSlug = useMemo(() => {
    const entry = attachmentValues[GIT_REPO_ATTACHMENT_ID];
    const config = entry?.config as GitRepoAttachmentConfig | undefined;
    return config?.repoSlug ?? '';
  }, [attachmentValues]);
  const availableBlueprints = useAvailableBlueprints(blueprints, repoSlug);

  const normalizedBlueprintSelection =
    blueprintSelection ??
    ({ mode: 'name', value: blueprintName ?? '' } satisfies BlueprintSelection);
  // A picked snapshot owns the chip — blueprint and snapshot are mutually
  // exclusive launch sources, so the snapshot id is the displayed value.
  const selectedBlueprintValue = snapshotId
    ? snapshotPickerValue(snapshotId)
    : normalizedBlueprintSelection.mode === 'id'
      ? (normalizedBlueprintSelection.value ?? '__none__')
      : (resolveBlueprintName(normalizedBlueprintSelection.value, availableBlueprints) ??
        '__none__');

  // Maps a blueprint chip selection (`__none__`, a blueprint id, or
  // `snapshot:<id>`) back to the parent's `BlueprintSelection` /
  // `onSnapshotChange`, and auto-fills the git-repo attachment when the
  // picked blueprint is repo-scoped and no repo has been chosen yet, so the
  // launch context lines up with what the blueprint was built for.
  const handleBlueprintValueChange = (v: string) => {
    if (!v) return;
    const pickedSnapshotId = snapshotIdFromPickerValue(v);
    if (pickedSnapshotId) {
      onSnapshotChange?.(pickedSnapshotId);
      onBlueprintChange(null);
      return;
    }
    if (snapshotId) onSnapshotChange?.(null);
    if (v === '__none__') {
      onBlueprintChange(null);
      return;
    }
    const blueprint = availableBlueprints.find((bp) => bp.id === v);
    onBlueprintChange(normalizedBlueprintSelection.mode === 'id' ? v : (blueprint?.name ?? null));
    const blueprintRepo = blueprint?.metadata?.repo;
    if (blueprintRepo && !repoSlug) {
      onAttachmentChange(
        GIT_REPO_ATTACHMENT_ID,
        { repoSlug: blueprintRepo } satisfies GitRepoAttachmentConfig,
        'github',
      );
    }
  };

  // Whether anything inside Advanced departs from the defaults. It marks the
  // collapsed header with a "customized" badge so an override is never silently
  // hidden, and — unless the caller asked for a collapsed start — opens the
  // section on mount so configuration that's actively shaping the run is on
  // screen. A fresh form (no blueprint pin, no resource size) always starts
  // collapsed so the form stays compact.
  const hasAdvancedOverride = useMemo(() => {
    if (resourceSize) return true;
    if (customSize) return true;
    if (dockerd) return true;
    if (computerUse) return true;
    if (idleTimeMinutes) return true;
    if (snapshotId) return true;
    // Labels and a group live in the same collapsed section, so they count
    // toward the badge for the same reason the sandbox pins do: a launch that
    // will file its run somewhere must not look like a default one.
    if (labelIds?.length) return true;
    if (groupId) return true;
    if (normalizedBlueprintSelection.mode === 'id') {
      return normalizedBlueprintSelection.value != null;
    }
    return normalizedBlueprintSelection.value.length > 0;
  }, [
    resourceSize,
    customSize,
    dockerd,
    computerUse,
    idleTimeMinutes,
    snapshotId,
    labelIds,
    groupId,
    normalizedBlueprintSelection,
  ]);
  const [advancedOpen, setAdvancedOpen] = useState(!advancedStartsCollapsed && hasAdvancedOverride);
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);

  const settingsChip = !hideSettingsChip ? (
    <AgentSettingsChipContainer
      agentType={agentType}
      selectedProviderId={selectedProviderId}
      model={model}
      modelEndpointId={modelEndpointId}
      reasoningEffort={reasoningEffort}
      onAgentTypeChange={onAgentTypeChange}
      onModelChange={onModelChange}
      onReasoningEffortChange={onReasoningEffortChange}
      onEndpointChange={onEndpointChange}
      launchableEndpointsOverride={launchableEndpointsOverride}
      providerSecretId={providerSecretId}
      onProviderSecretChange={onProviderSecretChange}
      providerKeyOwner={providerKeyOwner}
      showMissingKeyError={showMissingKeyError}
      missingKeyErrorText={missingKeyErrorText}
    />
  ) : null;

  const advancedChips = (
    <AdvancedConfigChips
      blueprints={availableBlueprints}
      snapshots={onSnapshotChange ? snapshots : undefined}
      blueprintValue={selectedBlueprintValue}
      repoSlug={repoSlug}
      onBlueprintValueChange={handleBlueprintValueChange}
      resourceSize={resourceSize}
      onResourceSizeChange={onResourceSizeChange}
      customSize={customSize}
      onCustomSizeChange={onCustomSizeChange}
      customSizes={customSizes}
      defaultSandboxSize={defaultSandboxSize}
      isTrial={isTrial}
      dockerd={dockerd}
      onDockerdChange={onDockerdChange}
      computerUse={computerUse}
      onComputerUseChange={onComputerUseChange}
      idleTimeMinutes={idleTimeMinutes}
      onIdleTimeMinutesChange={onIdleTimeMinutesChange}
      envVars={envVars}
      onEnvVarsChange={onEnvVarsChange}
      hasStoredSecrets={hasStoredSecrets}
      secretsOwner={secretsOwner}
      labelIds={labelIds}
      onLabelIdsChange={onLabelIdsChange}
      groupId={groupId}
      onGroupIdChange={onGroupIdChange}
    />
  );

  const attachmentsEditor = !hideAttachments ? (
    <AgentAttachmentsEditor
      attachments={attachmentValues}
      onAttachmentChange={onAttachmentChange}
    />
  ) : null;

  if (bare) {
    return (
      <div className="space-y-4">
        {settingsChip}
        {advancedChips}
        {attachmentsEditor}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {settingsChip}

      {attachmentsEditor ? (
        <CollapsibleConfigSection
          id="agent-config-attachments"
          title="Attachments"
          open={attachmentsOpen}
          onToggle={() => setAttachmentsOpen((v) => !v)}
        >
          {attachmentsEditor}
        </CollapsibleConfigSection>
      ) : null}

      <CollapsibleConfigSection
        id="agent-config-advanced"
        title="Advanced"
        open={advancedOpen}
        onToggle={() => setAdvancedOpen((v) => !v)}
        trailing={
          hasAdvancedOverride && !advancedOpen ? (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              customized
            </span>
          ) : undefined
        }
      >
        {advancedChips}
      </CollapsibleConfigSection>
    </div>
  );
}
